// ** React Imports
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

// ** MUI Imports
import { AttachMoney, Logout, Person } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Divider,
  Menu,
  MenuItem,
  Typography,
  styled
} from '@mui/material'

// **Custom Component Imports
import Icon from '../../icon'
import SwitchTenant from './switch-tenant'

// **Third party Imports

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux'
import {
  logout,
  setTenantList,
  setUserInformation
} from '../../../store/slice/auth'
import {
  setSelectedWorkspace,
  setWorkspaces
} from '../../../store/slice/workspace'

// **API Imports
import { logoutUser } from '../../../axios/api/auth'
import { getListOfTenants } from '../../../axios/api/tenant'

// **Utils Imports
import {
  APP_MODE,
  CANDIDATE_APP_MODE,
  CANDIDATE_APP_MODE_COOKIE_NAME,
  DOMAIN,
  RESERVED_SUBDOMAINS,
  ROLES,
  ROLES_LABEL,
  SUBSCRIPTION_PLANS
} from '../../../utils/constant'
import {
  checkCandidateAccess,
  checkCurrentRole,
  checkDeveloperAccess,
  deleteCookie,
  getInitials,
  getUserDetails,
  showErrorToast
} from '../../../utils/utils'

// **Context Imports
import { SettingsContext } from '../../../context/setting'
import useDemoUser from '../../../hooks/useDemoUser'
import { resetAttemptFilters } from '../../../store/slice/home'
import DemoGuard from '../../demo-guard'
import LearnerToggle from '../learner-toggle'

// ** Styled Components
const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  '&:hover .MuiBox-root, &:hover .MuiBox-root svg': {
    color: theme.palette.text.primary
  }
}))

// **static style
const styles = {
  px: 4,
  py: 1.75,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  '& svg': {
    mr: 1.5,
    fontSize: '1.5rem',
    color: 'text.secondary'
  }
}

const UserDropdown = ({ addBackgroundColor }) => {
  // Redux State
  const userData = useSelector((state) => state.auth.userData)
  const profile = useSelector((state) => state.auth.profile)
  const appMode = useSelector((state) => state.auth.appMode)
  const tenantList = useSelector((state) => state.auth.tenantList)

  //  States
  const [anchorEl, setAnchorEl] = useState(null)

  // Hooks
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { contestUri } = useParams()
  const location = useLocation()
  const { isDemoUser, isDemoTenant } = useDemoUser()

  // Context
  const { saveSettings } = useContext(SettingsContext)

  // Redux State
  const currentlyActivePlan = useSelector(
    (state) => state.auth.currentlyActivePlan
  )

  const candidateAppMode = useSelector(
    (state) => state.candidate.candidateAppMode
  )

  // variable

  const globalCandidatePath = useMemo(() => {
    const path = location.pathname?.toLowerCase()
    return path.endsWith('/c') || path.endsWith('/c/') || path.includes('/c/')
  }, [location.pathname])

  const isValidSubDomainLabel = useMemo(() => {
    const hostnameParts = window.location.hostname.split('.')

    if (hostnameParts.length <= 2) {
      return false
    }

    const subDomain = hostnameParts[0].toLowerCase()
    return !RESERVED_SUBDOMAINS.includes(subDomain)
  }, [])

  const isAdminRole = useMemo(() => checkCurrentRole(ROLES.ADMIN), [])

  const isCandidate = useMemo(() => checkCandidateAccess(), [])

  const isDeveloper = useMemo(() => checkDeveloperAccess(), [])

  const isLearner = useMemo(() => checkCurrentRole(ROLES.LEARNER), [])

  const isLearnerMode = useMemo(() => appMode === APP_MODE?.LEARNER, [appMode])

  const isEnterisePlan = useMemo(
    () => SUBSCRIPTION_PLANS.ENTERPRISE === currentlyActivePlan?.planName,
    [currentlyActivePlan]
  )

  const showToggle = useMemo(() => {
    return !isDeveloper && !isCandidate && !isLearner
  }, [isDeveloper, isCandidate, isLearner])

  const showContrastText = useMemo(() => {
    return contestUri || isCandidate || addBackgroundColor
  }, [contestUri, isCandidate, addBackgroundColor])

  const userDetails = useMemo(() => getUserDetails(), [])

  const canCandidateBuy = useMemo(() => {
    if (!isCandidate) {
      return false
    }

    if (candidateAppMode === CANDIDATE_APP_MODE.INTERVIEW) {
      return false
    }
    return Boolean(userDetails?.isLinkedinVerified)
  }, [isCandidate, candidateAppMode, userDetails])

  //handlers
  const navigateToURL = useCallback(
    (url) => {
      navigate(url)
    },
    [navigate]
  )

  const handleDropdownOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleDropdownClose = useCallback(
    (url) => {
      setAnchorEl(null)
      if (url) {
        setTimeout(() => {
          navigateToURL(url)
        })
      }
    },
    [navigateToURL]
  )

  const clearSelectedWorkspace = useCallback(() => {
    dispatch(setSelectedWorkspace(null))
    dispatch(setWorkspaces({ workspaces: null }))
  }, [dispatch])

  const setTenantToken = useCallback(
    (token) => {
      dispatch(setUserInformation({ jwt: token }))
      navigate('/')
      window.location.reload()
    },
    [dispatch, navigate]
  )

  const handleLogout = useCallback(async () => {
    if (!isDeveloper) {
      await logoutUser()
    }

    clearSelectedWorkspace()
    dispatch(logout())
    dispatch(resetAttemptFilters())

    if (contestUri || globalCandidatePath) {
      deleteCookie(CANDIDATE_APP_MODE_COOKIE_NAME, `.${DOMAIN}`)
    }

    if (!isValidSubDomainLabel && !contestUri && !isDeveloper) {
      saveSettings({ tenantConfig: null })
    }
    handleDropdownClose(
      contestUri
        ? `/c/${contestUri}/login`
        : globalCandidatePath
          ? `/candidates/login`
          : '/login'
    )
  }, [
    dispatch,
    contestUri,
    isDeveloper,
    saveSettings,
    handleDropdownClose,
    globalCandidatePath,
    isValidSubDomainLabel,
    clearSelectedWorkspace
  ])

  const fetchTenants = useCallback(async () => {
    getListOfTenants().then((response) => {
      if (response?.isError) {
        dispatch(setTenantList([]))
        showErrorToast(response)
      } else {
        dispatch(setTenantList(response))
      }
    })
  }, [dispatch])

  //UseEffects
  useEffect(() => {
    if (!isCandidate && !isDeveloper && !isDemoUser) {
      fetchTenants()
    }
  }, [fetchTenants, isCandidate, isDeveloper, isDemoUser])

  return (
    <Box>
      <Box className="header-user-details-section" onClick={handleDropdownOpen}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {profile?.profilePicUri ? (
            <Box className="user-profile-picture-container">
              <img
                src={profile?.profilePicUri}
                alt="User profile"
                className="user-profile-picture"
              />
            </Box>
          ) : (
            <Avatar
              alt={userData?.name || ''}
              src="/"
              sx={{ width: '2.5rem', height: '2.5rem' }}
            >
              {getInitials(userData?.name || '')}
            </Avatar>
          )}
          <Box
            sx={{
              display: 'flex',
              ml: 2,
              alignItems: 'flex-start',
              flexDirection: 'column'
            }}
          >
            {userData?.name && (
              <Typography
                title={userData?.name}
                sx={{
                  fontWeight: 500,
                  color: showContrastText
                    ? 'var(--primary-contrastText)'
                    : 'inherit'
                }}
                className="profile-user-name"
              >
                {userData?.name || ''}
              </Typography>
            )}
            {!isCandidate && (
              <Typography
                className="role-sub-heading"
                sx={{
                  color: showContrastText
                    ? 'var(--primary-contrastText) !important'
                    : 'inherit'
                }}
              >
                {ROLES_LABEL?.[userData?.roles] || ''}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleDropdownClose()}
        sx={{ '& .MuiMenu-paper': { minWidth: '230px', mt: 1.8 } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {tenantList?.length > 1 &&
          !isDeveloper &&
          !isCandidate &&
          !isDemoUser && (
            <SwitchTenant
              data={tenantList}
              selectedTenant={userData?.tenantId}
              setTenantToken={setTenantToken}
            />
          )}

        {showToggle && !isDemoUser && <LearnerToggle />}

        {(tenantList?.length > 1 || showToggle) &&
          !isDeveloper &&
          !isCandidate &&
          !isDemoUser && (
            <Divider sx={{ my: (theme) => `${theme.spacing(1)} !important` }} />
          )}

        {(isAdminRole || canCandidateBuy) && !isLearnerMode && (
          <MenuItemStyled
            sx={{ p: 0 }}
            onClick={() => handleDropdownClose()}
            component={Link}
            to={
              isAdminRole
                ? '/subscription?active=credit'
                : '/c/subscription?active=credit'
            }
          >
            <Box sx={styles}>
              <AttachMoney />
              Buy Credits
            </Box>
          </MenuItemStyled>
        )}

        {isAdminRole && !isLearnerMode && (
          <MenuItemStyled
            component={Link}
            onClick={() => handleDropdownClose()}
            to={'/subscription?active=Invoices'}
            sx={{
              p: 0
            }}
          >
            <Box
              sx={{
                ...styles
              }}
            >
              <Icon icon="ix:history-list" /> Invoices
            </Box>
          </MenuItemStyled>
        )}

        {!isLearnerMode && isAdminRole && (
          <Divider sx={{ my: (theme) => `${theme.spacing(1)} !important` }} />
        )}
        <MenuItemStyled
          sx={{ p: 0 }}
          component={Link}
          onClick={() => handleDropdownClose()}
          to={
            globalCandidatePath || contestUri
              ? '/c/profile'
              : '/settings/profile'
          }
        >
          <Box sx={styles}>
            <Person />
            Profile
          </Box>
        </MenuItemStyled>

        {isAdminRole && isEnterisePlan && !isLearnerMode && (
          <MenuItemStyled
            sx={{ p: 0 }}
            component={Link}
            to={'/settings/config'}
            onClick={() => handleDropdownClose()}
          >
            <Box sx={styles}>
              <Icon icon="icon-park-outline:setting-config" />
              Configurations
            </Box>
          </MenuItemStyled>
        )}

        <Divider sx={{ my: (theme) => `${theme.spacing(1)} !important` }} />
        <DemoGuard enableGuard={isDemoTenant}>
          <MenuItemStyled sx={{ p: 0 }} onClick={handleLogout}>
            <Box sx={styles}>
              <Logout />
              Logout
            </Box>
          </MenuItemStyled>
        </DemoGuard>
      </Menu>
    </Box>
  )
}

export default memo(UserDropdown)
